import { cn } from "@/utils/cn";
import { Props } from "@/utils/types/types";
import { Pagination as MantinePagination, Select } from "@mantine/core";
import { useSearchParams } from "react-router-dom";

export const getRequestPagination = ({
  request,
  pageSize: initialPageSize = 100,
}: {
  request: Request;
  pageSize?: number;
}) => {
  const { searchParams, pathname } = new URL(request.url);
  const params = Object.fromEntries(searchParams.entries());
  const pagination = {
    page: params.page ? Number(params.page) : 1,
    pageSize: params.pageSize ? Number(params.pageSize) : initialPageSize,
  };

  let paginationUrl: string | null = null;

  if (!params.page || !params.pageSize) {
    searchParams.set("page", `${pagination.page}`);
    searchParams.set("pageSize", `${pagination.pageSize}`);
    paginationUrl = `${pathname}?${searchParams.toString()}`;
  }

  return { paginationUrl, pagination };
};

type PaginationProps = Props<"className"> & {
  page: number;
  pageSize: number;
  total?: number;
  pageParamName?: string;
  pageSizeParamName?: string;
  pageSizeOptions?: string[];
  showPageSizeSelect?: boolean;
};

export default function Pagination({
  page,
  pageSize,
  total,
  className,
  pageParamName = "page",
  pageSizeParamName = "pageSize",
  pageSizeOptions = ["50", "100", "200", "500"],
  showPageSizeSelect = true,
}: PaginationProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const resolvedTotal = total ?? Math.ceil((page + 1) / 100) * 100;
  return (
    <div className={cn(className, "flex gap-6")}>
      {showPageSizeSelect && (
        <Select
          className="max-w-[80px]"
          styles={{ input: { height: 32, minHeight: 32 } }}
          value={`${pageSize}`}
          data={pageSizeOptions}
          onChange={nextPageSize => {
            if (!nextPageSize) return;
            searchParams.set(pageSizeParamName, nextPageSize);
            setSearchParams(searchParams);
          }}
        />
      )}
      <MantinePagination
        total={resolvedTotal}
        value={page}
        onChange={nextPage => {
          searchParams.set(pageParamName, nextPage.toString());
          setSearchParams(searchParams);
        }}
      />
    </div>
  );
}
